import type { ParsedReplayPacket } from './parseReplayContents'

export type ReplayEventType =
  | 'death'
  | 'damage'
  | 'respawn'
  | 'dimension_change'
  | 'chat'
  | 'command'
  | 'afk'
  | 'teleport'
  | 'kill_player'
  | 'kill_mob'
  | 'low_activity'
  | 'high_activity'

export interface ReplayEvent {
  type: ReplayEventType
  time: number
  packetIndex: number
  label?: string
  duration?: number
}

// ── C→S packet sets for activity detection ──────────────────────────────────
/* Movement packets are sent every tick even when the player is not touching
   anything, so they only count as activity when the position or the camera
   actually moved. `flying` / `player` carry no coordinates at all — they can
   never prove activity, and are listed only so they are not mistaken for one
   of the other groups. */
const MOVEMENT_NAMES = new Set([
  'player_position', 'player_look', 'position', 'look',
  'player_position_and_look', 'position_and_look', 'position_look',
  'flying', 'player',
])

/** Low-activity packets that are a deliberate action by themselves. */
const LOW_ACTIVITY_NAMES = new Set([
  'steer_vehicle', 'steer_boat', 'vehicle_move',
  'held_item_slot', 'held_item_change',
  'entity_action',
])

const MOVE_EPSILON = 0.01 // blocks
const LOOK_EPSILON = 0.1  // degrees

/* Packet name → merge group. Packets of one group collapse into a single bar
   while they keep arriving; different groups never swallow each other.
   `click_window` / `close_window` are one group per the spec, and the interact
   aliases are just older names of `use_entity`. */
const HIGH_ACTIVITY_GROUPS = new Map<string, string>([
  ['use_item', 'use_item'],
  ['block_place', 'block_place'],
  ['block_dig', 'block_dig'],
  ['arm_animation', 'arm_animation'],
  ['use_entity', 'use_entity'],
  ['interact', 'use_entity'],
  ['interact_at', 'use_entity'],
  ['click_window', 'window'],
  ['close_window', 'window'],
])

/* No activity for this long and the player is AFK. The same number closes a
   low-activity bar, so a bar always runs at least 10 s past its last packet —
   "длится минимум 10 сек, далее включается режим AFK". */
const AFK_TIMEOUT_MS = 10_000
/** Same-kind peaks closer than this fold into one bar. */
const HIGH_ACTIVITY_MERGE_MS = 1_000
const TELEPORT_THRESHOLD   = 32
const KILL_WINDOW_MS       = 1_500
/** damage_event and the update_health drop describe the same hit. */
const DAMAGE_DEDUPE_MS     = 500

export async function extractReplayEvents (packets: ParsedReplayPacket[]): Promise<ReplayEvent[]> {
  const events: ReplayEvent[] = []

  // ── State ────────────────────────────────────────────────────────────────
  let lastHealth = 20
  let myEntityId = -1

  // Entity type tracking: entityId → 'player' | 'mob'
  const playerEntityIds = new Set<number>()
  const playerUuidToEntityId = new Map<string, number>()

  // Kill detection: entityId → { time, isPlayer }
  const recentAttacks = new Map<number, { time: number; isPlayer: boolean }>()

  // Activity segments
  let lowStart = -1
  let lowIdx   = 0
  /** Last packet of any kind that proved the player was doing something. */
  let lastActivity = -1

  // Last position/camera the client reported, for real-change detection
  let sentX: number | null = null
  let sentY: number | null = null
  let sentZ: number | null = null
  let sentYaw: number | null = null
  let sentPitch: number | null = null
  /** group → bar being accumulated, flushed once the group goes quiet for 1 s */
  const highOpen = new Map<string, { time: number; last: number; packetIndex: number }>()

  let lastDamageAt = -Infinity

  // Position tracking for teleport (server → client)
  let lastSrvX: number | null = null
  let lastSrvZ: number | null = null

  // ── Helpers ──────────────────────────────────────────────────────────────
  function flushLowActivity (end: number): void {
    if (lowStart < 0) return
    events.push({
      type: 'low_activity',
      time: lowStart,
      packetIndex: lowIdx,
      label: 'Малая активность',
      duration: Math.max(0, end - lowStart),
    })
    lowStart = -1
  }

  /** True when the client actually moved or turned since its last report.
      The first report of each kind only sets the baseline — knowing where the
      player stands is not the same as seeing them move. */
  function movedForReal (params: any): boolean {
    const { x, y, z, yaw, pitch } = params ?? {}
    let moved = false
    if (typeof x === 'number' && typeof z === 'number') {
      if (sentX !== null) {
        moved ||= Math.abs(x - sentX) > MOVE_EPSILON ||
          Math.abs(z - sentZ!) > MOVE_EPSILON ||
          (typeof y === 'number' && Math.abs(y - sentY!) > MOVE_EPSILON)
      }
      sentX = x; sentZ = z
      if (typeof y === 'number') sentY = y
    }
    if (typeof yaw === 'number' && typeof pitch === 'number') {
      if (sentYaw !== null) {
        moved ||= Math.abs(yaw - sentYaw) > LOOK_EPSILON ||
          Math.abs(pitch - sentPitch!) > LOOK_EPSILON
      }
      sentYaw = yaw; sentPitch = pitch
    }
    return moved
  }

  /* Every proof of activity feeds one clock. A gap longer than the AFK timeout
     closes the running low-activity bar and fills the rest of the gap with an
     AFK band. High activity counts as activity but has its own bars, so it just
     ends the low one. */
  function markActivity (time: number, index: number, kind: 'low' | 'high'): void {
    if (lastActivity >= 0 && time - lastActivity > AFK_TIMEOUT_MS) {
      const afkFrom = lastActivity + AFK_TIMEOUT_MS
      flushLowActivity(afkFrom)
      events.push({
        type: 'afk',
        time: afkFrom,
        packetIndex: index,
        label: 'AFK',
        duration: time - afkFrom,
      })
    }
    lastActivity = time
    if (kind === 'high') {
      flushLowActivity(time)
      return
    }
    if (lowStart < 0) {
      lowStart = time
      lowIdx   = index
    }
  }

  function flushHighActivity (group: string): void {
    const open = highOpen.get(group)
    if (!open) return
    highOpen.delete(group)
    events.push({
      type: 'high_activity',
      time: open.time,
      packetIndex: open.packetIndex,
      label: 'Активность',
      duration: open.last - open.time,
    })
  }

  // ── Main loop ────────────────────────────────────────────────────────────
  let lastYield = performance.now()
  for (let i = 0; i < packets.length; i++) {
    // Time-based yield so the tab doesn't freeze on huge replays
    if (i % 500 === 0 && performance.now() - lastYield > 16) {
      lastYield = performance.now()
      await new Promise(resolve => setTimeout(resolve, 0))
    }
    const packet = packets[i]!
    const time   = packet.time ?? 0

    // ── C → S ──────────────────────────────────────────────────────────────
    if (!packet.isFromServer) {
      const name = packet.name

      // Chat and commands are only tracked for the recorded player: these are
      // the packets the client itself sent, so foreign chat never lands here.
      if (name === 'chat' || name === 'chat_message') {
        const msg: string = packet.params?.message ?? packet.params?.text ?? ''
        if (msg.startsWith('/')) {
          events.push({ type: 'command', time, packetIndex: i, label: msg.slice(0, 60) })
        } else if (msg.trim()) {
          events.push({ type: 'chat', time, packetIndex: i, label: msg.slice(0, 80) })
        }
      }

      // 1.19+ splits commands into their own packet
      if (name === 'chat_command' || name === 'chat_command_signed') {
        const cmd: string = packet.params?.command ?? ''
        if (cmd.trim()) {
          events.push({ type: 'command', time, packetIndex: i, label: `/${cmd}`.slice(0, 60) })
        }
      }

      // Attack: track which entity was hit for kill detection (no separate event)
      if ((name === 'use_entity' || name === 'interact') &&
          (packet.params?.mouse === 1 || packet.params?.type === 1)) {
        const targetId: number = packet.params?.entityId ?? packet.params?.target ?? -1
        const isPlayer = playerEntityIds.has(targetId)
        if (targetId >= 0) {
          recentAttacks.set(targetId, { time, isPlayer })
        }
      }

      // Low-activity tracking — movement only counts when something changed
      if (MOVEMENT_NAMES.has(name)) {
        if (movedForReal(packet.params)) markActivity(time, i, 'low')
      } else if (LOW_ACTIVITY_NAMES.has(name)) {
        markActivity(time, i, 'low')
      }

      // High-activity tracking: same-kind peaks within a second become one bar
      const group = HIGH_ACTIVITY_GROUPS.get(name)
      if (group) {
        markActivity(time, i, 'high')
        const open = highOpen.get(group)
        if (open && time - open.last <= HIGH_ACTIVITY_MERGE_MS) {
          open.last = time
        } else {
          if (open) flushHighActivity(group)
          highOpen.set(group, { time, last: time, packetIndex: i })
        }
      }

      continue
    }

    // ── S → C ──────────────────────────────────────────────────────────────
    switch (packet.name) {

      // ── Player spawn — track entity IDs that belong to players ────────────
      case 'named_entity_spawn': {
        const eid: number = packet.params?.entityId ?? -1
        if (eid >= 0) playerEntityIds.add(eid)
        const uuid: string = packet.params?.playerUUID ?? ''
        if (uuid) playerUuidToEntityId.set(uuid, eid)
        break
      }

      // ── player_info may carry entityId in some protocol versions ──────────
      case 'player_info':
      case 'player_info_v2': {
        const data: any[] = packet.params?.data ?? []
        for (const entry of data) {
          const uuid: string  = entry.uuid ?? ''
          const eid: number   = entry.entityId ?? -1
          if (eid >= 0) {
            playerEntityIds.add(eid)
            if (uuid) playerUuidToEntityId.set(uuid, eid)
          }
        }
        break
      }

      // ── Own entity id — needed to tell "took damage" from world noise ─────
      case 'login': {
        const eid: number = packet.params?.entityId ?? -1
        if (eid >= 0) myEntityId = eid
        break
      }

      // ── Damage (1.19.4+): the direct clientbound signal ───────────────────
      // Fires for every entity in range, so only the recorded player and the
      // entities they just hit are kept — anything else is background combat.
      case 'damage_event': {
        const eid: number = packet.params?.entityId ?? -1
        const isSelf = eid >= 0 && eid === myEntityId
        const isTarget = recentAttacks.has(eid) && (time - recentAttacks.get(eid)!.time) <= KILL_WINDOW_MS
        if ((isSelf || isTarget) && time - lastDamageAt > DAMAGE_DEDUPE_MS) {
          events.push({
            type: 'damage',
            time,
            packetIndex: i,
            label: isSelf ? 'Получен урон' : 'Нанесён урон',
          })
          lastDamageAt = time
        }
        break
      }

      // ── Kill detection via entity_status (status 3 = death) ──────────────
      case 'entity_status': {
        const eid: number    = packet.params?.entityId ?? -1
        const status: number = packet.params?.entityStatus ?? packet.params?.status ?? -1
        if (status === 3 && eid >= 0 && recentAttacks.has(eid)) {
          const atk = recentAttacks.get(eid)!
          if (time - atk.time <= KILL_WINDOW_MS) {
            events.push({
              type: atk.isPlayer ? 'kill_player' : 'kill_mob',
              time,
              packetIndex: i,
              label: atk.isPlayer ? 'Убийство игрока' : 'Убийство моба',
            })
          }
          recentAttacks.delete(eid)
        }
        break
      }

      // ── Teleport: server forces position (large jump) ─────────────────────
      case 'position':
      case 'player_position_and_look': {
        const x: number = packet.params?.x ?? 0
        const z: number = packet.params?.z ?? 0
        if (lastSrvX !== null && lastSrvZ !== null) {
          const dx = x - lastSrvX
          const dz = z - lastSrvZ
          const dist = Math.sqrt(dx * dx + dz * dz)
          if (dist >= TELEPORT_THRESHOLD) {
            events.push({
              type: 'teleport',
              time,
              packetIndex: i,
              label: `Телепорт (+${Math.round(dist)} б)`,
            })
          }
        }
        lastSrvX = x
        lastSrvZ = z
        break
      }

      // ── Health / death ────────────────────────────────────────────────────
      case 'update_health': {
        const health = packet.params?.health ?? 20
        if (health <= 0 && lastHealth > 0) {
          events.push({ type: 'death', time, packetIndex: i, label: 'Смерть' })
        } else if (health < lastHealth && health > 0 && (lastHealth - health) >= 1
          && time - lastDamageAt > DAMAGE_DEDUPE_MS) {
          // Pre-1.19.4 fallback: no damage_event, so the health drop is the signal
          events.push({ type: 'damage', time, packetIndex: i, label: `Получен урон (${Math.round(lastHealth - health)} ❤)` })
          lastDamageAt = time
        }
        lastHealth = health
        break
      }

      // ── Dimension change / respawn ────────────────────────────────────────
      case 'respawn': {
        const dim = packet.params?.dimension ?? packet.params?.dimensionName ?? ''
        const isOverworld = String(dim).includes('overworld')
        const isNether    = String(dim).includes('nether') || String(dim).includes('the_nether')
        const isEnd       = String(dim).includes('end')    || String(dim).includes('the_end')
        let label = 'Телепорт'
        if (isNether) label = 'Нижний мир'
        else if (isEnd) label = 'Энд'
        else if (isOverworld) label = 'Обычный мир'
        events.push({
          type: (isNether || isEnd || isOverworld) ? 'dimension_change' : 'respawn',
          time,
          packetIndex: i,
          label,
        })
        lastHealth = 20
        lastSrvX = null
        lastSrvZ = null
        break
      }

      default:
        break
    }
  }

  // Flush anything still open at the end of the replay. Silence that runs into
  // the end of the recording is AFK too, clipped to the last packet.
  const endTime = packets.at(-1)?.time ?? 0
  if (lastActivity >= 0) {
    const afkFrom = lastActivity + AFK_TIMEOUT_MS
    flushLowActivity(Math.min(afkFrom, endTime))
    if (endTime > afkFrom) {
      events.push({
        type: 'afk',
        time: afkFrom,
        packetIndex: packets.length - 1,
        label: 'AFK',
        duration: endTime - afkFrom,
      })
    }
  }
  for (const group of highOpen.keys()) flushHighActivity(group)

  // Sort by time (activity segments may be out of order relative to point events)
  events.sort((a, b) => a.time - b.time)

  return events
}
